import React, { useRef, useState } from "react";
import { HeadingMedium, BodyText } from "../central/Typography";

export default function MobilePlayerDrawer({
  open,
  onClose,
  onPlayerOpen,
  positions,
  players,
  team,
  budget,
  maxPlayers,
  totalSelectedCount,
  mobilePickerPosition,
  setMobilePickerPosition,
  mobilePickerSort,
  setMobilePickerSort,
  mobileIsLocked,
  wouldBreakCompositionIfAdded,
  toggleInSlot,
  getEffectivePrice,
  captainID,
  handleSetCaptainID,
  formRankMap,
  availablePlayerIDs = null,
}) {
  const getLast3Form = (player) => {
    const history = player?.pointsHistory || {};
    const gwKeys = Object.keys(history).sort((a, b) => {
      const aNum = Number(String(a).replace("GW", "")) || 0;
      const bNum = Number(String(b).replace("GW", "")) || 0;
      return aNum - bNum;
    });
    return gwKeys.slice(-3).map((gw) => Number(history[gw]) || 0);
  };

  const getFormDisplay = (player) => {
    const vals = getLast3Form(player);
    return vals.length ? [...vals].reverse().join(" • ") : "—";
  };

  const getPerAppearance = (value, appearances) => {
    const num = Number(value) || 0;
    const apps = Number(appearances) || 0;
    if (!apps) return 0;
    return Number((num / apps).toFixed(2));
  };

  const mobilePickerPlayers = (() => {
    const basePlayers =
      mobilePickerPosition === "ALL"
        ? [...players]
        : players.filter((p) => p.position === mobilePickerPosition);

    const isSelectedForSort = (player) => {
      const slot =
        mobilePickerPosition === "ALL" ? player.position : mobilePickerPosition;
      return Boolean(team[slot]?.includes(player.id));
    };

    return [...basePlayers].sort((a, b) => {
      if (mobilePickerSort === "price") {
        const aPrice = getEffectivePrice(a, "current");
        const bPrice = getEffectivePrice(b, "current");
        if (bPrice !== aPrice) return bPrice - aPrice;
        return (a.name || "").localeCompare(b.name || "");
      }

      if (mobilePickerSort === "totalPoints") {
        const aPoints = Number(a?.stats?.totalPoints || 0);
        const bPoints = Number(b?.stats?.totalPoints || 0);
        if (bPoints !== aPoints) return bPoints - aPoints;
        return (a.name || "").localeCompare(b.name || "");
      }

      if (mobilePickerSort === "goals") {
        const aGoals = Number(a?.stats?.goals || 0);
        const bGoals = Number(b?.stats?.goals || 0);
        if (bGoals !== aGoals) return bGoals - aGoals;
        return (a.name || "").localeCompare(b.name || "");
      }

      if (mobilePickerSort === "assists") {
        const aAssists = Number(a?.stats?.assists || 0);
        const bAssists = Number(b?.stats?.assists || 0);
        if (bAssists !== aAssists) return bAssists - aAssists;
        return (a.name || "").localeCompare(b.name || "");
      }

      if (mobilePickerSort === "ptsPerApp") {
        const aPtsPerApp = getPerAppearance(a?.stats?.totalPoints, a?.stats?.appearances);
        const bPtsPerApp = getPerAppearance(b?.stats?.totalPoints, b?.stats?.appearances);
        if (bPtsPerApp !== aPtsPerApp) return bPtsPerApp - aPtsPerApp;
        return (a.name || "").localeCompare(b.name || "");
      }

      if (mobilePickerSort === "formRank") {
        const aRank = Number(formRankMap?.get(a.id) || 999);
        const bRank = Number(formRankMap?.get(b.id) || 999);
        if (aRank !== bRank) return aRank - bRank;
        return (a.name || "").localeCompare(b.name || "");
      }

      if (mobilePickerSort === "name") {
        return (a.name || "").localeCompare(b.name || "");
      }

      if (mobilePickerSort === "selected") {
        const aSelected = isSelectedForSort(a);
        const bSelected = isSelectedForSort(b);
        if (aSelected !== bSelected) return aSelected ? -1 : 1;
        if (mobilePickerPosition === "ALL") {
          const aPos = a.position || "";
          const bPos = b.position || "";
          if (aPos !== bPos) return aPos.localeCompare(bPos);
        }
        return (a.name || "").localeCompare(b.name || "");
      }

      if (mobilePickerPosition === "ALL") {
        const aPos = a.position || "";
        const bPos = b.position || "";
        if (aPos !== bPos) return aPos.localeCompare(bPos);
      }
      return (a.name || "").localeCompare(b.name || "");
    });
  })();

  const renderDrawerStatSummary = (player) => {
    const stats = player?.stats || {};
    const selectionStats = player?.selectionStats || {};
    const formDisplay = getFormDisplay(player);
    const lastDelta = Number(player?.priceDelta || 0);
    const pointsPerApp = getPerAppearance(
      stats.totalPoints,
      stats.appearances
    );
    const pickedBy = Number(selectionStats.selectedByTeams || 0);
    const pickedPct = Number(selectionStats.selectedPct || 0);
    const isKeeperStyle = Number(stats.saves || 0) > 0;

    return (
      <div style={{ marginTop: "0.55rem" }}>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "0.5rem",
            marginBottom: "0.35rem",
          }}
        >
          <span style={{ fontWeight: "700", fontSize: "0.9rem", color: "#111" }}>
            {Number(stats.totalPoints || 0)} pts
          </span>
          <span style={{ fontWeight: "700", fontSize: "0.9rem", color: "#111" }}>
            Form: {formDisplay}
          </span>
          <span style={{ fontWeight: "700", fontSize: "0.9rem", color: "#111" }}>
            Picked by {pickedBy} ({pickedPct}%)
          </span>
        </div>

        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
          {isKeeperStyle ? (
            <>
              <span style={{ fontSize: "0.82rem", color: "#456" }}>
                SAV {Number(stats.saves || 0)}
              </span>
              <span style={{ fontSize: "0.82rem", color: "#456" }}>
                CS {Number(stats.cleanSheets || 0)}
              </span>
            </>
          ) : (
            <>
              <span style={{ fontSize: "0.82rem", color: "#456" }}>
                G {Number(stats.goals || 0)}
              </span>
              <span style={{ fontSize: "0.82rem", color: "#456" }}>
                A {Number(stats.assists || 0)}
              </span>
            </>
          )}
          <span style={{ fontSize: "0.82rem", color: "#456" }}>
            Apps {Number(stats.appearances || 0)}
          </span>
          <span style={{ fontSize: "0.82rem", color: "#456" }}>
            {pointsPerApp} pts/app
          </span>
        </div>
      </div>
    );
  };

  const sheetRef = useRef(null);
  const touchStartYRef = useRef(null);
  const touchCurrentYRef = useRef(null);
  const dragEnabledRef = useRef(false);
  const [dragOffsetY, setDragOffsetY] = useState(0);
  const [isDraggingSheet, setIsDraggingSheet] = useState(false);

  const handleTouchStart = (e) => {
    const sheetEl = sheetRef.current;
    const startY = e.touches?.[0]?.clientY ?? null;
    touchStartYRef.current = startY;
    touchCurrentYRef.current = startY;
    dragEnabledRef.current = !sheetEl || sheetEl.scrollTop <= 0;
  };

  const handleTouchMove = (e) => {
    if (!dragEnabledRef.current) return;

    const currentY = e.touches?.[0]?.clientY ?? null;
    touchCurrentYRef.current = currentY;

    const startY = touchStartYRef.current;
    if (typeof startY !== "number" || typeof currentY !== "number") return;

    const deltaY = currentY - startY;
    if (deltaY <= 0) {
      setDragOffsetY(0);
      return;
    }

    if (typeof e.preventDefault === "function") {
      e.preventDefault();
    }

    setIsDraggingSheet(true);
    setDragOffsetY(deltaY);
  };

  const handleTouchEnd = () => {
    const startY = touchStartYRef.current;
    const endY = touchCurrentYRef.current;
    const deltaY =
      typeof startY === "number" && typeof endY === "number"
        ? endY - startY
        : 0;

    const shouldClose = dragEnabledRef.current && deltaY > 110;

    touchStartYRef.current = null;
    touchCurrentYRef.current = null;
    dragEnabledRef.current = false;
    setIsDraggingSheet(false);

    if (shouldClose) {
      setDragOffsetY(0);
      onClose?.();
      return;
    }

    setDragOffsetY(0);
  };

  if (!open) return null;

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1200,
        background: "rgba(0,0,0,0.32)",
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
        overscrollBehavior: "contain",
      }}
    >
      <div
        ref={sheetRef}
        onClick={(e) => e.stopPropagation()}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{
          width: "100%",
          maxWidth: "520px",
          maxHeight: "76vh",
          overflowY: "auto",
          overscrollBehavior: "contain",
          background: "#fff",
          borderTopLeftRadius: "22px",
          borderTopRightRadius: "22px",
          padding: "0.9rem 1rem 1rem",
          boxSizing: "border-box",
          boxShadow: "0 -10px 30px rgba(0,0,0,0.18)",
          transform: `translateY(${dragOffsetY}px)`,
          transition: isDraggingSheet ? "none" : "transform 180ms ease-out",
          WebkitOverflowScrolling: "touch",
          willChange: "transform",
        }}
      >
        <div
          style={{
            width: "44px",
            height: "5px",
            borderRadius: "999px",
            background: "#d1d5db",
            margin: "0 auto 0.8rem",
          }}
        />

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "0.75rem",
            marginBottom: "0.8rem",
          }}
        >
          <HeadingMedium style={{ fontSize: "1.1rem", margin: 0 }}>
            Pick Players
          </HeadingMedium>
          <button
            type="button"
            onClick={onClose}
            style={{
              border: "none",
              background: "transparent",
              fontSize: "0.95rem",
              fontWeight: "600",
              color: "#555",
              cursor: "pointer",
            }}
          >
            Done
          </button>
        </div>

        <div
          style={{
            display: "flex",
            gap: "0.45rem",
            marginBottom: "0.85rem",
            overflowX: "auto",
          }}
        >
          {[...positions, "ALL"].map((pos) => (
            <button
              key={pos}
              type="button"
              onClick={() => setMobilePickerPosition(pos)}
              style={{
                border: "none",
                borderRadius: "999px",
                padding: "0.5rem 0.9rem",
                fontSize: "0.9rem",
                fontWeight: "700",
                whiteSpace: "nowrap",
                background:
                  mobilePickerPosition === pos ? "#50B4C8" : "#eef2f4",
                color: mobilePickerPosition === pos ? "#fff" : "#234",
                cursor: "pointer",
              }}
            >
              {pos}
            </button>
          ))}
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.4rem",
            marginBottom: "0.85rem",
            padding: "0 0.1rem",
            fontSize: "0.82rem",
            fontWeight: "600",
            color: "#666",
          }}
        >
          <span style={{ color: budget < -0.05 ? "#d32f2f" : "#111" }}>
            {budget < -0.05
              ? `-$${Math.abs(budget).toFixed(1)}m over`
              : `$${Math.abs(budget) < 0.05 ? "0.0" : budget.toFixed(1)}m left`}
          </span>
          <span style={{ color: "#b0b7c3" }}>•</span>
          <span>
            {totalSelectedCount}/{maxPlayers || "-"} selected
          </span>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            marginBottom: "0.85rem",
            padding: "0 0.1rem",
          }}
        >
          <BodyText
            style={{
              margin: 0,
              fontSize: "0.82rem",
              fontWeight: "600",
              color: "#666",
            }}
          >
            Sort
          </BodyText>
          <select
            value={mobilePickerSort}
            onChange={(e) => setMobilePickerSort(e.target.value)}
            style={{
              border: "1px solid #d9e0e6",
              borderRadius: "999px",
              padding: "0.35rem 0.7rem",
              fontSize: "0.82rem",
              fontWeight: "600",
              color: "#234",
              background: "#fff",
            }}
          >
            <option value="selected">Selected</option>
            <option value="totalPoints">Total Points</option>
            <option value="formRank">Form Rank</option>
            <option value="ptsPerApp">Pts/App</option>
            <option value="goals">Goals</option>
            <option value="assists">Assists</option>
            <option value="price">Price</option>
            <option value="name">Name</option>
          </select>
        </div>

        <div style={{ overflowY: "visible", maxHeight: "none", paddingBottom: "0.25rem" }}>
          {mobilePickerPlayers.map((player) => {
            const playerSlot =
              mobilePickerPosition === "ALL"
                ? player.position
                : mobilePickerPosition;
            const checked = team[playerSlot]?.includes(player.id);
            const disabled =
              mobileIsLocked ||
              (!checked && wouldBreakCompositionIfAdded(player, playerSlot));

            return (
              <div
                key={player.id}
                style={{
                  border: checked ? "1px solid #cfeaf0" : "1px solid #e5e7eb",
                  borderRadius: "14px",
                  padding: "0.8rem 0.9rem",
                  marginBottom: "0.6rem",
                  opacity: disabled ? 0.55 : 1,
                  background: checked ? "#f6fcfd" : "#fff",
                  boxShadow: checked ? "inset 3px 0 0 #50B4C8" : "none",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    gap: "0.75rem",
                  }}
                >
                                    <button
                    type="button"
                    onClick={() => onPlayerOpen?.(player)}
                    style={{
                      minWidth: 0,
                      border: "none",
                      background: "transparent",
                      padding: 0,
                      textAlign: "left",
                      cursor: "pointer",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <BodyText style={{ margin: 0, fontWeight: "700", fontSize: "0.98rem", color: "#111" }}>
                        {player.name}
                      </BodyText>
                      {availablePlayerIDs?.has(player.id) && (
                        <span style={{
                          width: 8, height: 8, borderRadius: "50%",
                          background: "#4CAF50", display: "inline-block", flexShrink: 0,
                        }} aria-label="Available" />
                      )}
                    </div>
                    <BodyText style={{ margin: "0.15rem 0 0", fontSize: "0.82rem", color: "#666" }}>
                      {player.position} • ${Number(getEffectivePrice(player, "current") || 0).toFixed(1)}m • Form Rank #{formRankMap.get(player.id) || "-"}
                    </BodyText>
                  </button>

                  <button
                    type="button"
                    disabled={disabled}
                    onClick={() => toggleInSlot(player.id, playerSlot, !checked)}
                    style={{
                      border: "none",
                      borderRadius: "999px",
                      padding: "0.5rem 0.8rem",
                      background: checked ? "#50B4C8" : "#eef2f4",
                      color: checked ? "#fff" : "#234",
                      fontWeight: "700",
                      fontSize: "0.84rem",
                      cursor: disabled ? "default" : "pointer",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {checked ? "✓ Selected" : "+ Add"}
                  </button>
                </div>

                <button
                  type="button"
                  onClick={() => onPlayerOpen?.(player)}
                  style={{
                    width: "100%",
                    border: "none",
                    background: "transparent",
                    padding: 0,
                    textAlign: "left",
                    cursor: "pointer",
                  }}
                >
                  {renderDrawerStatSummary(player)}
                </button>

                {checked && (
                  <div
                    style={{
                      marginTop: "0.6rem",
                      display: "flex",
                      gap: "0.45rem",
                      flexWrap: "wrap",
                    }}
                  >
                    <button
                      type="button"
                      disabled={mobileIsLocked}
                      onClick={() => handleSetCaptainID(player.id)}
                      style={{
                        borderRadius: "999px",
                        padding: "0.38rem 0.7rem",
                        fontWeight: "700",
                        fontSize: "0.8rem",
                        cursor: mobileIsLocked ? "default" : "pointer",
                        ...(captainID === player.id
                          ? { border: "none", background: "#E07856", color: "#fff" }
                          : { border: "1px solid #E07856", background: "transparent", color: "#E07856" }),
                      }}
                    >
                      {captainID === player.id ? "Captain" : "Make Captain"}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}